import { useState, useEffect, useRef } from "react";

const DEFAULT_SPEED_MS = 30;
const MAX_ANIMATION_DURATION_MS = 3500;

/**
 * Hook that progressively reveals text word-by-word, creating a typewriter effect.
 *
 * - Splits on whitespace boundaries to keep markdown structure intact
 * - Accelerates for long responses to cap total animation at ~3.5 seconds
 * - Returns fullText immediately when enabled=false (for historical messages)
 */
export function useTypewriterEffect(
  fullText: string,
  enabled: boolean,
  speed: number = DEFAULT_SPEED_MS
): {
  displayedText: string;
  isAnimating: boolean;
} {
  const [wordIndex, setWordIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const wordsRef = useRef<string[]>([]);
  const rafRef = useRef<number>(0);
  const lastTimeRef = useRef(0);
  const effectiveSpeedRef = useRef(speed);

  // Split text into words once when fullText changes
  useEffect(() => {
    if (!enabled) {
      wordsRef.current = [];
      setWordIndex(0);
      setIsAnimating(false);
      return;
    }

    const words = fullText.split(/(\s+)/);
    wordsRef.current = words;

    // Calculate effective speed: if total time would exceed cap, speed up
    const totalWords = words.length;
    const estimatedDuration = totalWords * speed;
    if (estimatedDuration > MAX_ANIMATION_DURATION_MS && totalWords > 0) {
      effectiveSpeedRef.current = MAX_ANIMATION_DURATION_MS / totalWords;
    } else {
      effectiveSpeedRef.current = speed;
    }

    setWordIndex(0);
    setIsAnimating(true);
    lastTimeRef.current = 0;
  }, [fullText, enabled, speed]);

  // Animation loop using requestAnimationFrame
  useEffect(() => {
    if (!isAnimating || !enabled) return;

    const words = wordsRef.current;
    const totalTokens = words.length;

    const tick = (timestamp: number) => {
      if (lastTimeRef.current === 0) {
        lastTimeRef.current = timestamp;
      }

      const elapsed = timestamp - lastTimeRef.current;
      const msPerToken = effectiveSpeedRef.current;

      if (elapsed >= msPerToken) {
        // How many tokens to advance
        const tokensToAdvance = Math.max(1, Math.floor(elapsed / msPerToken));
        const nextIndex = Math.min(wordIndex + tokensToAdvance, totalTokens);

        lastTimeRef.current = timestamp;
        setWordIndex(nextIndex);

        if (nextIndex >= totalTokens) {
          setIsAnimating(false);
          return;
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [isAnimating, enabled, wordIndex]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  if (!enabled) {
    return { displayedText: fullText, isAnimating: false };
  }

  const displayedText = wordsRef.current.slice(0, wordIndex).join("");

  return { displayedText, isAnimating };
}
